import { HeartPulse, Activity, Brain, Stethoscope, ArrowUpRight, CheckCircle2 } from "lucide-react";
import { ENAGA_PHOTOS } from "@/lib/localMedia";
import { useConfig } from "@/hooks/useConfig";
import { useDonation } from "@/context/DonationContext";

const ESPECIALIDADES = [
  { icon: Stethoscope, label: "Ortopedia" },
  { icon: Activity, label: "Fisioterapia" },
  { icon: Brain, label: "Psicologia" },
  { icon: HeartPulse, label: "Nutrição" },
];

const METRICAS = [
  { k: "2.450", v: "Atendimentos realizados" },
  { k: "6", v: "Especialidades ativas" },
  { k: "48h", v: "Tempo médio de resposta" },
  { k: "100%", v: "Gratuidade para assistidos" },
];

export default function PilarSaude() {
  const cfg = useConfig();
  const { openModal } = useDonation();
  const whatsappUrl = cfg.whatsapp_url || "#";

  return (
    <section
      id="pilar-saude"
      data-testid="pilar-saude-section"
      className="section-pad bg-white relative overflow-hidden"
    >
      <div className="absolute -top-10 -left-20 w-[28rem] h-[28rem] rounded-full bg-blue-500/10 blur-3xl" />

      <div className="container-ga relative">
        <div className="max-w-3xl mb-14">
          <div className="pill border-blue-200 text-blue-700 bg-blue-50 mb-6" data-testid="pilar-saude-pill">
            <HeartPulse className="w-3.5 h-3.5" />
            Pilar Saúde: ENAGA
          </div>
          <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-ga-ink">
            Saúde física e emocional que <span className="text-blue-700">chega ao bairro</span>.
          </h2>
          <p className="mt-6 text-ga-ink/70 text-lg leading-relaxed">
            A ENAGA é a clínica multiprofissional do Instituto. Oferecemos
            atendimento especializado, gratuito para os atendidos, em um
            ambiente preparado para cuidar de verdade.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 gap-4">
              <div className="relative rounded-3xl overflow-hidden aspect-square shadow-lg hover-lift">
                <img
                  src={ENAGA_PHOTOS.atendimento}
                  alt="Criança atendida pelo Instituto ENAGA com material pedagógico"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ga-amber">Atendimento especializado</div>
                  <div className="font-display font-bold text-lg leading-tight mt-1">
                    Cada criança tem seu plano de cuidado.
                  </div>
                </div>
              </div>
              <div className="relative rounded-3xl overflow-hidden aspect-square shadow-lg hover-lift">
                <img
                  src={ENAGA_PHOTOS.autismo}
                  alt="Campanha de conscientização do autismo do Instituto ENAGA"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ga-amber">Conscientização</div>
                  <div className="font-display font-bold text-lg leading-tight mt-1">
                    Inclusão e entendimento do autismo.
                  </div>
                </div>
              </div>
              <div className="relative col-span-2 rounded-3xl overflow-hidden aspect-[21/9] shadow-lg hover-lift">
                <img
                  src={ENAGA_PHOTOS.oficina}
                  alt="Oficina de culinária do Instituto ENAGA com crianças"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ga-ink/70 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 right-5 text-white">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-ga-amber">Saúde integral</div>
                  <div className="font-display font-bold text-xl leading-tight mt-1">
                    Nutrição, corpo e mente cuidados em um ambiente onde a criança se sente segura.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-5 lg:sticky lg:top-32 space-y-5">
            <div className="rounded-3xl bg-ga-ink text-white p-7 relative overflow-hidden">
              <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-blue-500/30 blur-3xl" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-blue-300 mb-4">
                  Impacto mensurável
                </div>
                <dl className="grid grid-cols-2 gap-5">
                  {METRICAS.map((m) => (
                    <div key={m.v} data-testid={`saude-metric-${m.v.replace(/\s/g, '-').toLowerCase()}`}>
                      <dt className="text-white/50 text-xs">{m.v}</dt>
                      <dd className="font-display font-black text-3xl text-white mt-1">{m.k}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </div>

            <div className="rounded-3xl border-2 border-blue-100 bg-blue-50/40 p-6">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-blue-700 mb-4">
                Especialidades
              </div>
              <ul className="grid grid-cols-2 gap-3">
                {ESPECIALIDADES.map((e) => (
                  <li key={e.label} className="flex items-center gap-2 text-ga-ink">
                    <e.icon className="w-4 h-4 text-blue-700" />
                    <span className="font-semibold text-sm">{e.label}</span>
                  </li>
                ))}
              </ul>
              <ul className="mt-5 space-y-2 text-sm text-ga-ink/70">
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700 shrink-0" /> Equipe multiprofissional cadastrada</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700 shrink-0" /> Atendimento humanizado e gratuito</li>
                <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-blue-700 shrink-0" /> Relatório entregue à família</li>
              </ul>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="saude-cta-schedule"
                className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-blue-700 text-white font-bold py-3.5 px-6 hover:bg-blue-800 transition-colors"
              >
                Agendar consulta
                <ArrowUpRight className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={() => openModal("saude-enaga")}
                data-testid="saude-cta-donate"
                className="flex-1 btn-primary"
              >
                Doar para a ENAGA
              </button>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}
